/**
 * Generic master-data entity config — drives one Hub/List/Form
 * implementation across all 6 tiled entities instead of near-duplicate
 * ones, matching apps/api/src/master-data/{party,product,uom,tax,warehouse,warehouse-location}.controller.ts's
 * identical CRUD shape. Field lists/labels mirror
 * apps/web/src/master-data/terminology.ts by hand (that package is
 * Node-only, not importable from this browser bundle) — pure presentation
 * data, not domain logic.
 *
 * dimension/dimension-value are deliberately excluded — RUNTIME_READY (full
 * CRUD exists) but intentionally hidden pending a terminology decision, per
 * the route inventory's Master Data section. Do not add a tile for them
 * without that decision.
 *
 * `kind: 'entity-picker'` (see product.baseUomId below) renders a real,
 * searchable `Combobox` of another entity's live records instead of a raw
 * text field for its foreign-key ID — see `EntityPickerField.tsx`. When
 * that entity's own list is empty (a fresh tenant with nothing seeded yet),
 * the picker offers an inline "create" shortcut through the real API
 * rather than dead-ending the form on an ID the user has no way to obtain.
 */
import type { SakhtarIconName } from '@sakhtar/design-system/react';

export type MasterDataEntityKind =
  'party' | 'product' | 'uom' | 'tax' | 'warehouse' | 'location' | 'cost_center' | 'price_list';

export type FieldKind =
  | 'text'
  | 'select'
  | 'number'
  | 'entity-picker'
  | 'multi-select'
  /** Gregorian ISO on the wire, Jalali picker on screen; blank clears it. */
  | 'date';

export interface FieldConfig {
  readonly key: string;
  readonly label: string;
  readonly kind: FieldKind;
  readonly options?: readonly {
    readonly value: string;
    readonly label: string;
  }[];
  readonly required?: boolean;
  /** Code the server generates when left blank on create (ERP-135); still required on edit. */
  readonly autoCode?: boolean;
  /**
   * Code with no server-side blank generation (tax, location): the form still opens with a
   * suggested next code so the field is never empty. Editable; uniqueness is checked on save.
   */
  readonly suggestCode?: boolean;
  /** `kind: 'entity-picker'` only — which other master-data entity this field references (e.g. product.baseUomId -> 'uom'). */
  readonly pickerEntity?: MasterDataEntityKind;
}

export interface EntityConfig {
  readonly kind: MasterDataEntityKind;
  readonly label: string;
  readonly labelPlural: string;
  readonly pathSegment: string;
  readonly idField: string;
  readonly icon: SakhtarIconName;
  /**
   * One-line hub-tile description. Copy matches the live design project's
   * `/t/kimia/master-data` frame (claude.ai/design/p/6c9c527c…, "Sakhtar
   * Cloud ERP - Final.dc.html", checked 2026-10-03) for the 5 entities that
   * frame shows; `location` has no frame there (it's a `warehouse`
   * sub-resource in the design) so its description is written in the same
   * style rather than left blank.
   */
  readonly hubDescription: string;
  readonly listColumns: readonly {
    readonly key: string;
    readonly label: string;
    /** Render the value as a Jalali date. */
    readonly date?: boolean;
  }[];
  readonly formFields: readonly FieldConfig[];
}

/**
 * `status` is genuinely optional at the domain layer for all 5 entities —
 * every `create*` function in
 * `packages/foundation/src/repositories/foundation-repos.ts` defaults it
 * (`status: input.status ?? 'active'`) and every `Create*Input` type in
 * `packages/foundation/src/types.ts` declares it `status?: EntityStatus`.
 * `required: true` here would mark a field the backend treats as optional
 * as required in the UI — `EntityFormPage.tsx` seeds a real `'active'`
 * default for create instead, matching the backend's own default rather
 * than forcing an explicit choice for something the API doesn't require.
 */
const STATUS_FIELD: FieldConfig = {
  key: 'status',
  label: 'وضعیت',
  kind: 'select',
  options: [
    { value: 'active', label: 'فعال' },
    { value: 'inactive', label: 'غیرفعال' },
  ],
  required: false,
};

export const MASTER_DATA_ENTITIES: readonly EntityConfig[] = [
  {
    kind: 'party',
    label: 'طرف‌حساب',
    labelPlural: 'طرف‌حساب‌ها',
    pathSegment: 'parties',
    idField: 'partyId',
    icon: 'building',
    hubDescription: 'مشتری، تأمین‌کننده و سایر طرف‌های حساب',
    listColumns: [
      { key: 'code', label: 'کد' },
      { key: 'name', label: 'نام' },
      { key: 'kind', label: 'نوع' },
      { key: 'roles', label: 'نقش' },
      { key: 'nationalId', label: 'کد / شناسه ملی' },
      { key: 'economicCode', label: 'کد اقتصادی' },
      { key: 'mobile', label: 'موبایل' },
      { key: 'phone', label: 'تلفن' },
      { key: 'email', label: 'ایمیل' },
      { key: 'postalCode', label: 'کد پستی' },
      { key: 'address', label: 'آدرس' },
    ],
    formFields: [
      { key: 'code', label: 'کد', kind: 'text', autoCode: true },
      { key: 'name', label: 'نام', kind: 'text', required: true },
      {
        key: 'kind',
        label: 'نوع',
        kind: 'select',
        required: true,
        options: [
          { value: 'person', label: 'شخص' },
          { value: 'organization', label: 'سازمان' },
        ],
      },
      // BUG-10: a Party's customer/supplier role — real party_role rows via
      // setPartyRoles, not a fabricated field. A Party can hold both.
      {
        key: 'roles',
        label: 'نقش',
        kind: 'multi-select',
        options: [
          { value: 'customer', label: 'مشتری' },
          { value: 'supplier', label: 'تأمین‌کننده' },
        ],
      },
      // Identity and telephone values must remain text to preserve leading zeros.
      {
        key: 'nationalId',
        label: 'کد ملی شخص / شناسه ملی سازمان',
        kind: 'text',
      },
      { key: 'economicCode', label: 'کد اقتصادی', kind: 'text' },
      { key: 'mobile', label: 'موبایل', kind: 'text' },
      { key: 'phone', label: 'تلفن', kind: 'text' },
      { key: 'email', label: 'ایمیل', kind: 'text' },
      { key: 'postalCode', label: 'کد پستی', kind: 'text' },
      { key: 'address', label: 'آدرس', kind: 'text' },
      STATUS_FIELD,
    ],
  },
  {
    kind: 'product',
    label: 'کالا',
    labelPlural: 'کالاها',
    pathSegment: 'products',
    idField: 'productId',
    icon: 'box',
    hubDescription: 'کد کالا، واحد پیش‌فرض و گروه‌بندی',
    listColumns: [
      { key: 'code', label: 'کد' },
      { key: 'name', label: 'نام' },
    ],
    formFields: [
      { key: 'code', label: 'کد', kind: 'text', autoCode: true },
      { key: 'name', label: 'نام', kind: 'text', required: true },
      {
        key: 'baseUomId',
        label: 'واحد سنجش پایه',
        kind: 'entity-picker',
        pickerEntity: 'uom',
        required: true,
      },
      STATUS_FIELD,
    ],
  },
  {
    kind: 'uom',
    label: 'واحد سنجش',
    labelPlural: 'واحدهای سنجش',
    pathSegment: 'uoms',
    idField: 'uomId',
    icon: 'layers',
    hubDescription: 'واحدها و ضریب تبدیل',
    listColumns: [
      { key: 'code', label: 'کد' },
      { key: 'name', label: 'نام' },
    ],
    formFields: [
      { key: 'code', label: 'کد', kind: 'text', autoCode: true },
      { key: 'name', label: 'نام', kind: 'text', required: true },
      STATUS_FIELD,
    ],
  },
  {
    kind: 'tax',
    label: 'مالیات',
    labelPlural: 'مالیات‌ها',
    pathSegment: 'taxes',
    idField: 'taxId',
    icon: 'invoice',
    hubDescription: 'نرخ‌های مالیات و عوارض',
    listColumns: [
      { key: 'code', label: 'کد' },
      { key: 'name', label: 'نام' },
      { key: 'rateNumerator', label: 'نرخ' },
    ],
    formFields: [
      { key: 'code', label: 'کد', kind: 'text', required: true, suggestCode: true },
      { key: 'name', label: 'نام', kind: 'text', required: true },
      {
        key: 'rateNumerator',
        label: 'صورت نرخ',
        kind: 'number',
        required: true,
      },
      {
        key: 'rateDenominator',
        label: 'مخرج نرخ',
        kind: 'number',
        required: true,
      },
      STATUS_FIELD,
    ],
  },
  {
    kind: 'warehouse',
    label: 'انبار',
    labelPlural: 'انبارها',
    pathSegment: 'warehouses',
    idField: 'warehouseId',
    icon: 'inventory',
    hubDescription: 'انبارها و محل‌های نگهداری',
    listColumns: [
      { key: 'code', label: 'کد' },
      { key: 'name', label: 'نام' },
    ],
    formFields: [
      { key: 'code', label: 'کد', kind: 'text', autoCode: true },
      { key: 'name', label: 'نام', kind: 'text', required: true },
      STATUS_FIELD,
    ],
  },
  {
    // BUG-11: warehouse_location already existed in the schema with the
    // right shape (WarehouseLocationController); this just gives it a
    // real CRUD screen instead of the placeholder /master-data/locations
    // used to fall back to.
    kind: 'location',
    label: 'مکان انبار',
    labelPlural: 'مکان‌های انبار',
    pathSegment: 'locations',
    idField: 'warehouseLocationId',
    icon: 'inventory',
    // No frame for this entity in the design (it's a `warehouse`
    // sub-resource there) — description written in the same style as the
    // other 5, not copied from a design source.
    hubDescription: 'مکان‌های نگهداری کالا درون انبارها',
    listColumns: [
      { key: 'code', label: 'کد' },
      { key: 'name', label: 'نام' },
    ],
    formFields: [
      {
        key: 'warehouseId',
        label: 'انبار',
        kind: 'entity-picker',
        pickerEntity: 'warehouse',
        required: true,
      },
      { key: 'code', label: 'کد', kind: 'text', required: true, suggestCode: true },
      { key: 'name', label: 'نام', kind: 'text', required: true },
      STATUS_FIELD,
    ],
  },
  {
    kind: 'cost_center',
    label: 'مرکز هزینه',
    labelPlural: 'مراکز هزینه',
    pathSegment: 'cost-centers',
    idField: 'costCenterId',
    icon: 'layers',
    hubDescription: 'مراکز هزینه برای درخواست خرید و گزارش هزینه',
    listColumns: [
      { key: 'code', label: 'کد' },
      { key: 'name', label: 'نام' },
    ],
    formFields: [
      { key: 'code', label: 'کد', kind: 'text', autoCode: true, suggestCode: true },
      { key: 'name', label: 'نام', kind: 'text', required: true },
      STATUS_FIELD,
    ],
  },
  {
    // The line grid (product, unit, price) is the PriceListLinesEditor section of
    // EntityFormPage; these are the list header fields.
    kind: 'price_list',
    label: 'لیست قیمت',
    labelPlural: 'لیست‌های قیمت',
    pathSegment: 'price-lists',
    idField: 'priceListId',
    icon: 'tag',
    hubDescription: 'قیمت فروش هر کالا به تفکیک واحد، با بازهٔ اعتبار',
    listColumns: [
      { key: 'code', label: 'کد' },
      { key: 'name', label: 'نام' },
      { key: 'effectiveFrom', label: 'از تاریخ', date: true },
      { key: 'effectiveTo', label: 'تا تاریخ', date: true },
      { key: 'lineCount', label: 'تعداد اقلام' },
    ],
    formFields: [
      { key: 'code', label: 'کد', kind: 'text', autoCode: true, suggestCode: true },
      { key: 'name', label: 'نام', kind: 'text', required: true },
      { key: 'effectiveFrom', label: 'شروع اعتبار', kind: 'date' },
      { key: 'effectiveTo', label: 'پایان اعتبار', kind: 'date' },
      STATUS_FIELD,
    ],
  },
];

/**
 * Looked up by `pathSegment` (the `:entity` route param, e.g. "parties"),
 * not `kind` (the singular domain name, e.g. "party") — the router and
 * every `<Link>` to a list/form page use `pathSegment`. Confirmed live
 * while capturing ERP-223 screenshots: matching on `kind` here meant this
 * always returned `undefined` and every entity list/form page rendered
 * blank, for all 5 entities.
 */
export function findEntityConfig(pathSegment: string | undefined): EntityConfig | undefined {
  return MASTER_DATA_ENTITIES.find((e) => e.pathSegment === pathSegment);
}
